import { useEffect, useRef, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Radio, MapPin, CalendarDays, Plus, RefreshCw, Loader2, Globe, Upload, Clock, ExternalLink, CalendarPlus, Trash2, Sparkles, Building2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { de } from "date-fns/locale";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { DocScanButton } from "@/components/DocScanButton";
import { RegFormDialog } from "@/components/RegFormDialog";

const WEBSITE_EVENT_ACCOUNTS = ["lokal.bruchkoebel"];
const CITY_EVENT_ACCOUNTS = ["bruchkoebel.beleben"];

const SOURCE_BADGE = {
  website: { label: "Webseite", className: "bg-blue-50 text-blue-700 border-blue-200" },
  stadt: { label: "Stadt", className: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  upload: { label: "Liste", className: "bg-violet-50 text-violet-700 border-violet-200" },
  "Instagram-Analyse": { label: "Instagram", className: "bg-pink-50 text-pink-700 border-pink-200" },
};

const REL = {
  high: { label: "Hohe Relevanz", className: "bg-red-100 text-red-700 border-red-200" },
  medium: { label: "Mittlere Relevanz", className: "bg-amber-100 text-amber-800 border-amber-200" },
  low: { label: "Niedrige Relevanz", className: "bg-neutral-100 text-neutral-600 border-neutral-200" },
};

export default function EventRadar() {
  const { accountKey } = useOutletContext();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [syncingWeb, setSyncingWeb] = useState(false);
  const [syncingCity, setSyncingCity] = useState(false);
  const [importing, setImporting] = useState(false);
  const [planTarget, setPlanTarget] = useState(null);
  const [planType, setPlanType] = useState("reel");
  const [planAI, setPlanAI] = useState(true);
  const [planSeries, setPlanSeries] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [regForms, setRegForms] = useState([]);
  const [regFormEvent, setRegFormEvent] = useState(null);
  const importRef = useRef();

  const isWebsiteAccount = WEBSITE_EVENT_ACCOUNTS.includes(accountKey);
  const isCityAccount = CITY_EVENT_ACCOUNTS.includes(accountKey);

  const load = () => api.get("/events", { params: { account: accountKey } }).then(({ data }) => setEvents(data));
  const loadForms = () => api.get("/reg-forms", { params: { account: accountKey } }).then(({ data }) => setRegForms(data)).catch(() => setRegForms([]));
  useEffect(() => {
    if (accountKey) {
      load();
      loadForms();
      api.get("/meta/status", { params: { account: accountKey } })
        .then(({ data }) => setConnected(!!data.connected)).catch(() => setConnected(false));
    }
  }, [accountKey]);

  const openPlan = (e) => {
    const count = events.filter(
      (x) => x.title === e.title && x.date && x.date >= new Date().toISOString().slice(0, 10),
    ).length;
    setPlanTarget({ ...e, seriesCount: count });
    setPlanType("reel");
    setPlanAI(true);
    setPlanSeries(false);
  };

  const submitPlan = async () => {
    if (!planTarget) return;
    setPlanning(true);
    try {
      if (planSeries) {
        const { data } = await api.post("/events/plan-series", { event_id: planTarget.id, content_type: planType, with_ai: planAI });
        setPlanTarget(null);
        const skipped = data.skipped ? ` · ${data.skipped} bereits vorhanden` : "";
        toast.success(`${data.created || 0} Ideen für die Reihe angelegt${skipped}`, {
          action: { label: "Zur Pipeline", onClick: () => navigate("/pipeline") },
        });
      } else {
        const { data } = await api.post(`/events/${planTarget.id}/plan-content`, { content_type: planType, with_ai: planAI });
        setPlanTarget(null);
        toast.success(data.existing ? "Für dieses Event gibt es bereits eine Pipeline-Karte" : (data.ai ? "Content-Idee mit KI-Entwurf angelegt" : "Content-Idee angelegt"), {
          action: { label: data.existing ? "Karte öffnen" : "Zur Pipeline", onClick: () => navigate(data.content?.id ? `/pipeline?focus=${data.content.id}` : "/pipeline") },
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Planen fehlgeschlagen");
    } finally {
      setPlanning(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Dieses Event wirklich löschen?")) return;
    setDeletingId(id);
    try {
      await api.delete(`/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success("Event gelöscht");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setDeletingId("");
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/events/refresh-from-instagram", { account_key: accountKey });
      await load();
      toast.success(`Events aktualisiert: ${data.upcoming || 0} anstehend, ${data.past || 0} im Rückblick`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktualisierung fehlgeschlagen");
    } finally {
      setRefreshing(false);
    }
  };

  const syncWeb = async () => {
    setSyncingWeb(true);
    try {
      const { data } = await api.post("/events/sync-from-web", { account_key: accountKey });
      await load();
      toast.success(`Von Webseite synchronisiert: ${data.upcoming || 0} anstehende Events`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Webseiten-Sync fehlgeschlagen");
    } finally {
      setSyncingWeb(false);
    }
  };

  const syncCity = async () => {
    setSyncingCity(true);
    try {
      const { data } = await api.post("/events/sync-from-city", { account_key: accountKey });
      await load();
      toast.success(`Stadt-Kalender synchronisiert: ${data.upcoming || 0} relevante Events`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stadt-Sync fehlgeschlagen");
    } finally {
      setSyncingCity(false);
    }
  };

  const importList = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("account_key", accountKey);
      fd.append("file", file);
      const { data } = await api.post("/events/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success(`${data.added || 0} Event(s) aus der Liste übernommen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1100px] mx-auto animate-rise">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 flex items-center gap-1.5">
            <Radio className="h-3.5 w-3.5" /> Event-Radar
          </p>
          <h1 className="font-head text-4xl font-bold text-neutral-900 tracking-tighter mt-1">Anstehende Events vor Ort</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isWebsiteAccount && (
            <button
              data-testid="sync-web-events-btn"
              onClick={syncWeb}
              disabled={syncingWeb}
              className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-60"
            >
              {syncingWeb ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
              Von Webseite aktualisieren
            </button>
          )}
          {isCityAccount && (
            <button
              data-testid="sync-city-events-btn"
              onClick={syncCity}
              disabled={syncingCity}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-indigo-700 transition disabled:opacity-60"
            >
              {syncingCity ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
              Stadt-Kalender aktualisieren
            </button>
          )}
          {!isWebsiteAccount && (
            <>
              <button
                data-testid="import-events-btn"
                onClick={() => importRef.current?.click()}
                disabled={importing}
                className="inline-flex items-center gap-2 bg-violet-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-violet-700 transition disabled:opacity-60"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Event-Liste hochladen
              </button>
              <input ref={importRef} type="file" accept="image/jpeg,image/png,image/webp,.csv,.xlsx,.xls" hidden onChange={importList} />
            </>
          )}
          {connected && (
            <button
              data-testid="refresh-events-btn"
              onClick={refresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 bg-white border border-neutral-300 text-neutral-700 text-sm font-medium px-4 py-2.5 rounded-lg hover:border-neutral-400 transition disabled:opacity-60"
            >
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Aus Instagram aktualisieren
            </button>
          )}
          <CreateEventDialog open={open} setOpen={setOpen} accountKey={accountKey} onCreated={load} />
        </div>
      </div>

      {!isWebsiteAccount && (
        <p className="text-xs text-neutral-400 mt-3">
          {isCityAccount && "Mit dem Stadt-Kalender-Button werden öffentliche Events der Stadt Bruchköbel geladen (KI-gefiltert auf publikumswirksame Termine). "}
          Lade alternativ eine Event-Liste als Foto/Screenshot oder Excel/CSV hoch — das System liest Datum, Titel, Uhrzeit, Ort und Beschreibung automatisch aus.
        </p>
      )}

      <div className="mt-6 space-y-3">
        {(() => {
          const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
          const upcoming = events.filter((e) => differenceInCalendarDays(parseISO(e.date), todayMid) >= 0);
          const past = events.filter((e) => differenceInCalendarDays(parseISO(e.date), todayMid) < 0);
          const Row = ({ e }) => {
            const days = differenceInCalendarDays(parseISO(e.date), new Date());
            const rel = REL[e.relevance] || REL.medium;
            const src = SOURCE_BADGE[e.source];
            const regForm = regForms.find((rf) => rf.event_id === e.id);
            return (
              <div data-testid={`event-row-${e.id}`} className={`bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-5 flex items-start gap-5 hover:shadow-md transition ${days < 0 ? "opacity-70" : ""}`}>
                <div className="text-center shrink-0 w-16">
                  <div className="font-head text-2xl font-bold text-neutral-900">{format(parseISO(e.date), "d")}</div>
                  <div className="text-xs text-neutral-400 uppercase">{format(parseISO(e.date), "MMM", { locale: de })}</div>
                  <div className={`mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${days < 0 ? "bg-neutral-100 text-neutral-400" : days <= 2 ? "bg-red-50 text-red-600" : "bg-neutral-100 text-neutral-500"}`}>
                    {days < 0 ? "vorbei" : days === 0 ? "heute" : `in ${days}T`}
                  </div>
                </div>
                {e.image_url && (
                  <img src={e.image_url} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0 border border-neutral-200" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-head font-semibold text-neutral-900 text-[17px]">{e.title}</h3>
                    <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${rel.className}`}>{rel.label}</span>
                    {src && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${src.className}`}>{src.label}</span>}
                    {e.source === "Instagram-Analyse" && e.date_certain === false && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Datum prüfen</span>
                    )}
                  </div>
                  {e.description && <p className="text-sm text-neutral-500 mt-1 line-clamp-2">{e.description}</p>}
                  <div className="flex items-center gap-4 mt-2 text-xs text-neutral-400 flex-wrap">
                    <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {format(parseISO(e.date), "EEEE, d. MMMM yyyy", { locale: de })}</span>
                    {e.time && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {e.time}{e.end_time ? `–${e.end_time}` : ""} Uhr</span>}
                    {e.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.location}</span>}
                    {e.link && (
                      <a href={e.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium">
                        <ExternalLink className="h-3.5 w-3.5" /> Details
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      data-testid={`plan-event-${e.id}`}
                      onClick={() => openPlan(e)}
                      className="inline-flex items-center gap-1.5 bg-neutral-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-neutral-800 transition"
                    >
                      <CalendarPlus className="h-3.5 w-3.5" /> Als Content-Idee planen
                    </button>
                    {regForm ? (
                      <button
                        data-testid={`regform-open-${e.id}`}
                        onClick={() => navigate(`/anmeldungen/${regForm.id}`)}
                        className="inline-flex items-center gap-1.5 bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-fuchsia-100 transition"
                      >
                        <ClipboardList className="h-3.5 w-3.5" />
                        Anmeldungen {regForm.counts?.approved ?? 0}/{regForm.quota}
                        {(regForm.counts?.pending ?? 0) > 0 && (
                          <span className="bg-amber-400 text-amber-950 rounded-full px-1.5 text-[10px] font-bold">{regForm.counts.pending} offen</span>
                        )}
                      </button>
                    ) : (
                      <button
                        data-testid={`regform-create-${e.id}`}
                        onClick={() => setRegFormEvent(e)}
                        className="inline-flex items-center gap-1.5 border border-neutral-200 text-neutral-600 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-neutral-50 transition"
                      >
                        <ClipboardList className="h-3.5 w-3.5" /> Anmeldeformular
                      </button>
                    )}
                    <button
                      data-testid={`delete-event-${e.id}`}
                      onClick={() => deleteEvent(e.id)}
                      disabled={deletingId === e.id}
                      title="Event löschen"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-neutral-400 hover:text-rose-500 hover:bg-rose-50 transition disabled:opacity-50"
                    >
                      {deletingId === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            );
          };
          return (
            <>
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-400">Anstehend ({upcoming.length})</p>
              {upcoming.map((e) => <Row key={e.id} e={e} />)}
              {upcoming.length === 0 && <div className="text-sm text-neutral-400 py-6">Keine anstehenden Events. Lege oben ein neues Event an.</div>}
              {past.length > 0 && (
                <div className="pt-4">
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-400 mb-3">Rückblick · vergangen ({past.length})</p>
                  <div className="space-y-3">{past.map((e) => <Row key={e.id} e={e} />)}</div>
                </div>
              )}
            </>
          );
        })()}
        {events.length === 0 && <div className="text-center text-neutral-400 py-16">Keine Events erfasst.</div>}
      </div>

      <Dialog open={!!planTarget} onOpenChange={(o) => !o && setPlanTarget(null)}>
        <DialogContent className="max-w-md" data-testid="plan-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Event als Content-Idee planen</DialogTitle>
            <DialogDescription>
              Aus „{planTarget?.title}" wird eine Idee in der Content-Pipeline – optional mit KI-Entwurf.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-neutral-600">Content-Format</label>
              <div className="flex gap-2 mt-1.5">
                {["reel", "story", "post"].map((t) => (
                  <button
                    key={t}
                    data-testid={`plan-type-${t}`}
                    onClick={() => setPlanType(t)}
                    className={`flex-1 capitalize text-sm py-2 rounded-lg border transition ${planType === t ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="plan-ai-toggle">
              <input type="checkbox" checked={planAI} onChange={(e) => setPlanAI(e.target.checked)} className="h-4 w-4 rounded accent-fuchsia-600" />
              <span className="text-sm text-neutral-700 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-fuchsia-500" /> Hook &amp; Caption per KI erstellen</span>
            </label>
            {planTarget?.seriesCount > 1 && (
              <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-indigo-200 bg-indigo-50 p-3" data-testid="plan-series-toggle">
                <input type="checkbox" checked={planSeries} onChange={(e) => setPlanSeries(e.target.checked)} className="h-4 w-4 mt-0.5 rounded accent-indigo-600" />
                <span className="text-sm text-neutral-700">
                  <span className="font-semibold text-indigo-700">Ganze Reihe planen ({planTarget.seriesCount} Termine)</span>
                  <span className="block text-xs text-neutral-500 mt-0.5">Dieses Event findet mehrfach statt — lege für jeden Termin eine eigene Idee an.</span>
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <button
              data-testid="plan-submit-btn"
              onClick={submitPlan}
              disabled={planning}
              className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-neutral-800 disabled:opacity-60"
            >
              {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
              {planSeries ? `Reihe anlegen (${planTarget?.seriesCount} Termine)` : (planAI ? "Idee mit KI-Entwurf anlegen" : "Idee anlegen")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RegFormDialog
        open={!!regFormEvent}
        onOpenChange={(o) => !o && setRegFormEvent(null)}
        accountKey={accountKey}
        event={regFormEvent}
        onSaved={(f) => { loadForms(); navigate(`/anmeldungen/${f.id}`); }}
      />
    </div>
  );
}

function CreateEventDialog({ open, setOpen, accountKey, onCreated }) {
  const [form, setForm] = useState({ title: "", date: "", location: "", relevance: "medium", description: "" });
  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Titel und Datum erforderlich");
    await api.post("/events", { account_key: accountKey, ...form });
    toast.success("Event angelegt");
    setOpen(false);
    setForm({ title: "", date: "", location: "", relevance: "medium", description: "" });
    onCreated();
  };
  const cls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-event-btn" className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800">
          <Plus className="h-4 w-4" /> Neues Event
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-head">Neues Event</DialogTitle>
          <DialogDescription>Erfasse ein anstehendes Event für den Event-Radar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <DocScanButton kind="event" onExtracted={(d) => setForm((s) => ({ ...s, ...d }))} />
          <input data-testid="event-title-input" placeholder="Titel" value={form.title} onChange={(e) => f("title", e.target.value)} className={cls} />
          <input data-testid="event-date-input" type="date" value={form.date} onChange={(e) => f("date", e.target.value)} className={cls} />
          <input placeholder="Ort" value={form.location} onChange={(e) => f("location", e.target.value)} className={cls} />
          <select value={form.relevance} onChange={(e) => f("relevance", e.target.value)} className={`${cls} bg-white`}>
            <option value="high">Hohe Relevanz</option>
            <option value="medium">Mittlere Relevanz</option>
            <option value="low">Niedrige Relevanz</option>
          </select>
          <textarea rows={3} placeholder="Beschreibung" value={form.description} onChange={(e) => f("description", e.target.value)} className={`${cls} h-auto py-2 resize-none`} />
        </div>
        <DialogFooter>
          <button data-testid="event-save-btn" onClick={submit} className="bg-neutral-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-neutral-800">Event speichern</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
